import {
  Component,
  effect,
  ElementRef,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Phase } from 'src/app/interfaces/phase';
import { Project } from 'src/app/interfaces/project';
import { Target } from 'src/app/interfaces/target';
import { Update } from 'src/app/interfaces/update';
import { PhaseService } from 'src/app/services/phase.service';
import { ProjectService } from 'src/app/services/project.service';
import { UpdateService } from 'src/app/services/update.service';
import { CurrentWhiteLabelApplication } from 'src/app/utils/current-white-label-application';
import { ProjectTabs } from '../../enums/project-tabs';
import { ModuleHelper } from 'src/app/utils/module-helper';
import { Module } from 'src/app/enums/module';
import { Subscription } from 'rxjs';
import { Share } from '@capacitor/share';
import { DeviceService } from '../../services/device.service';
import { HttpParams } from '@angular/common/http';
import { ParticipationService } from '../../services/participation.service';
import { TicketService } from 'src/app/services/ticket.service';
import { VideoService } from '../../services/video.service';
import { TargetPickerService } from '../../services/target-picker.service';
import { ProjectPlanningComponent } from './tabs/project-planning/project-planning.component';
import { StorageService } from 'src/app/services/storage.service';
import { InAppReview } from '@capacitor-community/in-app-review';
import * as Sentry from '@sentry/browser';
import { SmartReportingService } from 'src/app/services/smart-reporting.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AnalyticsType } from 'src/app/enums/analytics-type';
import { ProjectChildProjectsComponent } from './tabs/project-child-projects/project-child-projects.component';
import { ProjectAboutUsComponent } from './tabs/project-about-us/project-about-us.component';
import { ProjectSmartReportingComponent } from './tabs/project-smart-reporting/project-smart-reporting.component';
import { ProjectContactComponent } from './tabs/project-contact/project-contact.component';
import { ProjectParticipateComponent } from './tabs/project-participate/project-participate.component';
import { UpdateListComponent } from '../../components/update-list/update-list.component';
import { ProjectHomeComponent } from './tabs/project-home/project-home.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { ImgFallBackDirective } from '../../directives/img-fallback.directive';
import { LoaderComponent } from '../../components/loader/loader.component';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonRouterOutlet,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
  NavController,
  Platform,
} from '@ionic/angular/standalone';
import { DivClickDirective } from 'src/app/directives/div-click.directive';

@Component({
  selector: 'app-project',
  templateUrl: './project.page.html',
  standalone: true,
  imports: [
    LoaderComponent,
    NgIf,
    ImgFallBackDirective,
    InlineSVGModule,
    NgxSkeletonLoaderModule,
    NgFor,
    ProjectHomeComponent,
    UpdateListComponent,
    ProjectParticipateComponent,
    ProjectContactComponent,
    ProjectSmartReportingComponent,
    ProjectPlanningComponent,
    ProjectAboutUsComponent,
    ProjectChildProjectsComponent,
    DatePipe,
    TranslateModule,
    IonContent,
    IonBackButton,
    IonSegment,
    IonSegmentButton,
    DivClickDirective,
    IonHeader,
    IonTitle,
    IonToolbar,
    IonButtons,
  ],
})
export class ProjectPage {
  private performanceStartTime: number = 0;

  project: Project;
  phases: Phase[];
  latestUpdate: Update;
  updates: Update[];
  totalUpdates: number;
  companyText: string;
  projectLoaded: boolean = false;
  deviceDataLoaded: boolean = false;
  currentPage: string;
  targets: Target[];
  projectSlug: string;
  sticky: boolean = false;
  fromUrl: string;
  starSubscription: Subscription;
  canShare: boolean = false;
  navigationTab: string;
  loading: boolean = false;
  startLivestream: boolean = false;
  isProjectApp: boolean = false;
  updateDeviceData: any = {};
  ProjectTabs = ProjectTabs;
  @ViewChildren(IonSegmentButton) tabButtons: QueryList<IonSegmentButton>;
  scrolled: boolean = false;

  pages = [
    { value: ProjectTabs.HOME, title: 'project.tab.home', enabled: true },
    {
      value: ProjectTabs.UPDATES,
      title: 'project.tab.updates',
      enabled: false,
      unread: 0,
    },
    {
      value: ProjectTabs.PLANNING,
      title: 'project.tab.planning',
      enabled: false,
    },
    {
      value: ProjectTabs.PARTICIPATE,
      title: 'project.tab.participate',
      enabled: false,
      unread: 0,
    },
    {
      value: ProjectTabs.CONTACT,
      title: 'project.tab.contact',
      enabled: true,
      unread: 0,
    },
    {
      value: ProjectTabs.SMART_REPORTING,
      title: 'project.tab.smart-reporting',
      enabled: false,
      unread: 0,
    },
    {
      value: ProjectTabs.CHILD_PROJECTS,
      title: 'project.tab.child-projects',
      enabled: false,
    },
    {
      value: ProjectTabs.ABOUT_US,
      title: 'project.tab.about-us',
      enabled: true,
    },
  ];

  @ViewChild(IonContent) content: IonContent;
  @ViewChild('tabs') projectTabs: ElementRef;
  @ViewChild(IonHeader, { read: ElementRef }) ionHeader?: ElementRef;
  @ViewChild(ProjectPlanningComponent)
  public projectPlaningComponent: ProjectPlanningComponent;
  private backSubscription: Subscription;

  ionHeaderHeight: number;
  projectHeaderHeight: number;
  projectCoverHeight: number;
  projectTabsHeight: number;

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private updateService: UpdateService,
    private phaseService: PhaseService,
    private deviceService: DeviceService,
    private router: Router,
    private videoService: VideoService,
    private app: CurrentWhiteLabelApplication,
    private ticketService: TicketService,
    private smartReportingService: SmartReportingService,
    private targetPickerService: TargetPickerService,
    private participationService: ParticipationService,
    private storageService: StorageService,
    private analyticsService: AnalyticsService,
    private navCtrl: NavController,
    private translateService: TranslateService,
    ionRouterOutlet: IonRouterOutlet,
    platform: Platform
  ) {
    this.performanceStartTime = performance.now();
    this.isProjectApp = this.app.isProjectApp();

    if (this.isProjectApp) {
      this.projectSlug = this.app.getProjectAppSlug();
      projectService
        .loadProject(this.projectSlug)
        .then(this.logPerformance.bind(this));
    } else {
      const currentUrlSegment = this.route.snapshot.url.join('/');
      const isUrlEndingWithUpdates = currentUrlSegment.endsWith(
        ProjectTabs.UPDATES
      );

      if (
        this.router.getCurrentNavigation()?.extras.state?.tab ||
        isUrlEndingWithUpdates
      ) {
        this.navigationTab =
          this.router.getCurrentNavigation()?.extras.state?.tab ??
          ProjectTabs.UPDATES;
      }

      this.projectSlug = this.route.snapshot.params.slug;

      this.targetPickerService.starDone$.subscribe(
        () => (this.loading = false)
      );

      this.storageService.get('projects_opened').then((result) => {
        const value: number = result || 0;
        if (value === 3) {
          InAppReview.requestReview().catch((e) => {
            // Ignore review not working on web error,
            // because its easier then handle case when
            // we ask for review only on review capable devices.
            if (e.message !== 'Not implemented on web.') {
              throw e;
            }
          });
        }
        this.storageService.set('projects_opened', value + 1);
      });
    }

    effect(() => {
      if (this.projectService.isProjectLoading()) {
        return;
      }

      this.project = this.projectService.getProjectData();
      if (this.project) {
        this.loadProject().then(this.logPerformance.bind(this));

        this.analyticsService.logEvent(
          AnalyticsType.PROJECT_DETAIL_TAB_CHANGED,
          {
            to: this.currentPage,
          },
          this.project
        );
      }
    });

    this.starSubscription = this.projectService.starChanged$.subscribe(
      (project) => {
        if (project.id == this.project.id) {
          this.project.starred = project.starred;
        }
      }
    );

    this.ticketService.lastViewDataUpdated$.subscribe(() =>
      this.setPageUnread(ProjectTabs.CONTACT, 0)
    );
    this.smartReportingService.lastViewDataUpdated$.subscribe(() => {
      this.setPageUnread(ProjectTabs.SMART_REPORTING, 0);
    });
    this.participationService.lastViewDataUpdated$.subscribe(() =>
      this.setPageUnread(ProjectTabs.PARTICIPATE, 0)
    );

    Share.canShare().then(
      (result) => (this.canShare = result.value && !!app.getPwaDomain())
    );

    this.backSubscription = platform.backButton.subscribeWithPriority(
      1,
      (processNextHandler) => {
        if (!ionRouterOutlet.canGoBack()) {
          this.navCtrl.setDirection('back');
          this.router.navigate(['/'], { replaceUrl: true });
        } else {
          processNextHandler();
        }
      }
    );
  }

  checkHeights() {
    this.ionHeaderHeight = this.ionHeader?.nativeElement.offsetHeight || 0;
    this.projectTabsHeight = this.projectTabs?.nativeElement.offsetHeight || 0;
    this.projectCoverHeight =
      (document.querySelector('.project__header__cover') as HTMLElement)
        ?.offsetHeight || 0;
    this.projectHeaderHeight =
      (document.querySelector('.project__header') as HTMLElement)
        ?.offsetHeight || 0;
  }

  ngOnDestroy() {
    this.starSubscription.unsubscribe();
    this.backSubscription.unsubscribe();
  }

  async handleScroll(event) {
    if (this.projectTabs === undefined) {
      return;
    }
    this.checkHeights();
    this.scrolled =
      event.detail.scrollTop > this.projectCoverHeight - this.ionHeaderHeight;
    this.sticky =
      event.detail.scrollTop >
      this.projectHeaderHeight - this.ionHeaderHeight - this.projectTabsHeight;
  }

  onScrollEnd() {
    this.videoService.checkVideos();
  }

  private setPageUnread(tab: ProjectTabs, value: number) {
    const index = this.pages.findIndex((p) => p.value === tab);
    this.pages[index].unread = value;
  }

  private enablePage(tab: ProjectTabs) {
    const index = this.pages.findIndex((p) => p.value === tab);
    this.pages[index].enabled = true;
  }

  determineProjectPages() {
    if (!this.isProjectApp) {
      this.enablePage(ProjectTabs.UPDATES);
    }

    if (
      !this.isProjectApp &&
      (this.project.ratingThemes.length > 0 ||
        this.project.activeSurveys.length > 0)
    ) {
      this.enablePage(ProjectTabs.PARTICIPATE);
    }

    if (ModuleHelper.hasModule(this.project, Module.SMART_REPORTING)) {
      this.enablePage(ProjectTabs.SMART_REPORTING);
    }

    if (!!this.project.planning) {
      this.enablePage(ProjectTabs.PLANNING);
    }

    if (
      this.project.isParentProject &&
      this.project.childrenProjects.length > 0 &&
      ModuleHelper.hasModule(this.project, Module.CHILD_PROJECTS) &&
      !this.isProjectApp
    ) {
      this.enablePage(ProjectTabs.CHILD_PROJECTS);
    }

    this.currentPage = this.navigationTab
      ? this.navigationTab
      : this.pages[0].value;
    this.projectLoaded = true;

    this.checkHeights();
  }

  segmentChanged(event) {
    this.switchTab(event.detail.value);
  }

  async switchTab(tab) {
    const index = this.pages.findIndex((page) => page.value == tab);
    this.analyticsService.logEvent(
      AnalyticsType.PROJECT_DETAIL_TAB_CHANGED,
      {
        from: this.currentPage,
        to: this.pages[index].value,
      },
      this.project
    );

    this.currentPage = this.pages[index].value;

    const element = await this.content.getScrollElement();

    const contentStart =
      (document.querySelector('.project__content') as HTMLElement)?.offsetTop ||
      0;
    if (element.scrollTop > contentStart - this.projectTabsHeight) {
      this.content.scrollToPoint(0, contentStart - this.projectTabsHeight);
    }

    if (tab === ProjectTabs.UPDATES) {
      this.updateService.updateLastViewData(this.project);
      setTimeout(() => {
        this.setPageUnread(ProjectTabs.UPDATES, 0);
      }, 1000);
    }
  }

  goBack() {
    this.router.navigate([this.fromUrl], { replaceUrl: true });
  }

  async loadProject() {
    this.projectLoaded = false;

    try {
      this.phases = await this.phaseService.fetch(this.project);

      const params = new HttpParams().set('perPage', '21');
      const updateResponse = await this.updateService.getAllUpdates(
        this.project,
        params
      );
      this.updates = updateResponse.member;

      let updates = await this.updateService.filterUpdatesByTargetGroup(
        this.updates,
        this.project
      );

      updates = await this.getUpdateReactions(updates);
      const filteredUpdatesCount = this.updates.length - updates.length;
      this.updates = updates;
      this.totalUpdates = updateResponse.totalItems - filteredUpdatesCount;

      this.project.childrenProjects = this.project.childrenProjects.filter(
        (p) => p.status === 'PUBLISHED'
      );

      await this.getDeviceData();
      this.determineProjectPages();
      await this.getRatingThemes();
    } catch (error) {
      if (!(error instanceof Error)) {
        const errorDetails = {
          message: error.message || 'Unknown error',
          status: error.status || 'Unknown status',
          ...(error.headers && { headers: error.headers }),
        };
        error = new Error(JSON.stringify(errorDetails));
      }

      Sentry.captureException(error);
      this.router.navigate(['404'], { replaceUrl: true });
    }

    this.fixTabsForAria();
  }

  fixTabsForAria() {
    let initialized = false;
    const interval = setInterval(() => {
      this.tabButtons.forEach((btn, i) => {
        const el: HTMLElement = (btn as any).el;
        const button = el.shadowRoot.querySelector('button');
        if (!button) return;
        const link = document.createElement('a');
        link.innerText = el.innerText;
        link.className = 'button-native';
        let label = this.translateService.instant(this.pages[i].title);
        if (this.pages[i].unread)
          label +=
            ' (' + this.translateService.instant('project.tab.unread') + ')';
        link.setAttribute('aria-label', label);
        button.replaceWith(link);
        initialized = true;
      });
      if (initialized) clearInterval(interval);
    }, 500);
  }

  async getUpdateReactions(updates: Update[]): Promise<Update[]> {
    const reactions = await this.updateService.getUpdateReactionsForUpdates(
      updates
    );
    return updates.map((u: Update) => {
      u.likesPerType = reactions[u.id];
      u.likedBy = Object.values(u.likesPerType).reduce((c, n) => c + n, 0);
      return u;
    });
  }

  async getDeviceData() {
    let projectId = this.project.id.toString();
    let updateIds = this.updates.map(({ id }) => id).join(',');
    let params = new HttpParams()
      .set('project_ids', projectId)
      .set('update_ids', updateIds);

    try {
      const response = await this.deviceService.getData(params);

      const projectData = response.projects[projectId];

      this.project.distance = projectData.distance;
      this.project.starred = projectData.starred;
      this.project.hasCustomerAccess = projectData.hasCustomerAccess;

      this.setPageUnread(ProjectTabs.UPDATES, projectData.updatesUnreadCount);
      this.setPageUnread(
        ProjectTabs.PARTICIPATE,
        projectData.participationUnreadCount
      );
      this.setPageUnread(
        ProjectTabs.CONTACT,
        projectData.conversationsUnreadCount
      );
      this.setPageUnread(
        ProjectTabs.SMART_REPORTING,
        projectData.smartReportingNoticeUnreadCount
      );

      this.updateDeviceData = response.updates;

      if (this.updates.length) {
        if (projectData.target) {
          this.updates.forEach((update, index) => {
            if (
              update.targets &&
              update.targets.find(
                (target) => target.id == projectData.target.id
              )
            ) {
              this.updates.splice(index, 1);
            }
          });
        }

        this.updates.some((update) => {
          if (!update.ratingMonitorPush) {
            this.latestUpdate = update;
            return true;
          }
        });
      }
      this.project.amountOfFollowers = projectData.amountOfFollowers;
    } catch (error) {
    } finally {
      this.deviceDataLoaded = true;
    }
  }

  async getRatingThemes() {
    try {
      this.project.ratingThemes = await this.projectService.getRatingThemes(
        this.project
      );
    } catch (error) {
      // Ignore and use the themes already available
    }
  }

  goToParent() {
    this.router.navigate(['projects', this.project.parentProject.slug]);
  }

  async share() {
    await Share.share({
      url: `https://${this.app.getPwaDomain()}/projects/${this.project.slug}`,
    }).catch((e) => {
      // share cancelled
    });

    this.analyticsService.logEvent(
      AnalyticsType.PROJECT_SHARED,
      {},
      this.project
    );
  }

  async toggleFollowProject() {
    this.loading = true;
    if (this.project.starred) {
      await this.projectService.unstarProject(this.project);
      this.analyticsService.logEvent(
        AnalyticsType.PROJECT_UNFOLLOW,
        {},
        this.project
      );
      this.loading = false;
    } else {
      this.targetPickerService.presentTargets(this.project);
    }
  }

  public goToLivestream() {
    this.startLivestream = true;
    this.currentPage = this.pages[0].value;
    this.analyticsService.logEvent(
      AnalyticsType.LIVESTREAM_CLICKED,
      {},
      this.project
    );
  }

  public startedLivestream(event) {
    this.startLivestream = false;
  }

  private async logPerformance() {
    return this.analyticsService.logPerformance(
      AnalyticsType.PROJECT_LOADED,
      this.performanceStartTime,
      this.project
    );
  }
}
